import { persistentAtom } from "@nanostores/persistent";
import type { Hash } from "viem";
import { deserialize, serialize } from "wagmi";
import { LocalStorageKeys } from "./keys";
import type { BridgeTx, BridgeTxClaimedSuccess, BridgeTxFailed } from "./types";

export const $bridgeTxHistoryAtom = persistentAtom<BridgeTx[]>(LocalStorageKeys.BRIDGE_TX_HIST_KEY, [], {
  encode: serialize,
  decode: deserialize,
});

export const $bridgeTxFailedAtom = persistentAtom<BridgeTxFailed[]>(LocalStorageKeys.BRIDGE_TX_FAILED_KEY, [], {
  encode: serialize,
  decode: deserialize,
});

export function getBridgeTxHistory(): BridgeTx[] {
  return $bridgeTxHistoryAtom.get();
}

export function getBridgeTxFailedHistory(): BridgeTxFailed[] {
  return $bridgeTxFailedAtom.get();
}

export function storeBridgeTx(txData: BridgeTx) {
  const history = $bridgeTxHistoryAtom.get();
  const data = history.concat(txData);
  $bridgeTxHistoryAtom.set(data);
}

export function storeBridgeTxFailed(txData: BridgeTxFailed) {
  const history = $bridgeTxFailedAtom.get();
  const data = history.concat(txData);
  $bridgeTxFailedAtom.set(data);
}

/**
 * Updates an unclaimed tx as claimed
 *
 * @param id the id of the unclaimed tx
 * @param claimTxHash the hash of the claim tx
 * @param claimTimestamp the timestamp of the claim tx
 * @returns
 */
export function updateUnclaimedTx(
  id: string,
  claimTxHash: Hash,
  claimTimestamp: number,
): BridgeTxClaimedSuccess | undefined {
  const history = $bridgeTxHistoryAtom.get();

  // find unclaimed tx by id
  const unclaimedBridgeTx = history.filter((tx) => tx.state === "unclaimed").find((tx) => tx.id === id);

  if (!unclaimedBridgeTx) {
    console.log("Unclaimed bridge tx not found. Local storage may be lost.");
    return;
  }

  const claimedTxData: BridgeTxClaimedSuccess =
    unclaimedBridgeTx.bridgeType === "deposit"
      ? ({
          ...unclaimedBridgeTx,
          bridgeType: "deposit",
          state: "claimed",
          txStatus: "success",
          claimTimestamp,
          claimTxHash,
        } as const)
      : ({
          ...unclaimedBridgeTx,
          bridgeType: "withdraw",
          state: "claimed",
          txStatus: "success",
          claimTimestamp,
          claimTxHash,
        } as const);

  // find and replace object by id
  const updatedHistory = history.map((tx) => (tx.id === id ? claimedTxData : tx));
  $bridgeTxHistoryAtom.set(updatedHistory);

  return claimedTxData;
}
